if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.d2cPointers = new Map();
        this.d2cZoom = {
          activeMedia: null,
          container: null,
          image: null,
          scale: 1,
          x: 0,
          y: 0,
          startX: 0,
          startY: 0,
          panStartX: 0,
          panStartY: 0,
          startScale: 1,
          startDistance: 0,
        };
        this.onD2cPointerDown = this.onD2cPointerDown.bind(this);
        this.onD2cPointerMove = this.onD2cPointerMove.bind(this);
        this.onD2cPointerEnd = this.onD2cPointerEnd.bind(this);
        this.onD2cPopState = this.onD2cPopState.bind(this);
        this.onD2cHashChange = this.onD2cHashChange.bind(this);
      }

      hide(fromHistory = false) {
        this.disableD2cZoom();
        if (!fromHistory && this.d2cHistoryPushed && window.location.hash === '#media-viewer') {
          window.history.replaceState(
            window.history.state || {},
            '',
            `${window.location.pathname}${window.location.search}${this.d2cPreviousHash || ''}`
          );
        }
        this.d2cHistoryPushed = false;
        window.removeEventListener('popstate', this.onD2cPopState);
        window.removeEventListener('hashchange', this.onD2cHashChange);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.pushD2cHistoryState();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        this.enableD2cZoom(activeMedia, container);

        if (window.matchMedia('(min-width: 750px)').matches) {
          activeMedia.scrollIntoView();
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        } else {
          container.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      pushD2cHistoryState() {
        if (this.d2cHistoryPushed || !window.history || window.location.hash === '#media-viewer') return;

        this.d2cPreviousHash = window.location.hash;
        window.location.hash = 'media-viewer';
        this.d2cHistoryPushed = true;
        window.addEventListener('popstate', this.onD2cPopState);
        window.addEventListener('hashchange', this.onD2cHashChange);
      }

      onD2cPopState() {
        if (!this.hasAttribute('open') || window.location.hash === '#media-viewer') return;
        this.hide(true);
      }

      onD2cHashChange() {
        if (!this.hasAttribute('open') || window.location.hash === '#media-viewer') return;
        this.hide(true);
      }

      enableD2cZoom(activeMedia, container) {
        this.disableD2cZoom();
        const image = activeMedia?.matches?.('img') ? activeMedia : activeMedia?.querySelector?.('img');
        if (!image) return;

        this.d2cZoom.activeMedia = activeMedia;
        this.d2cZoom.container = container;
        this.d2cZoom.image = image;
        this.resetD2cZoom();

        image.addEventListener('pointerdown', this.onD2cPointerDown);
        image.addEventListener('pointermove', this.onD2cPointerMove);
        image.addEventListener('pointerup', this.onD2cPointerEnd);
        image.addEventListener('pointercancel', this.onD2cPointerEnd);
        image.addEventListener('lostpointercapture', this.onD2cPointerEnd);
      }

      disableD2cZoom() {
        const image = this.d2cZoom?.image;
        if (image) {
          image.removeEventListener('pointerdown', this.onD2cPointerDown);
          image.removeEventListener('pointermove', this.onD2cPointerMove);
          image.removeEventListener('pointerup', this.onD2cPointerEnd);
          image.removeEventListener('pointercancel', this.onD2cPointerEnd);
          image.removeEventListener('lostpointercapture', this.onD2cPointerEnd);
          image.style.transform = '';
        }

        this.d2cPointers.clear();
        this.classList.remove('product-media-modal--zoomed');
        this.d2cZoom.activeMedia = null;
        this.d2cZoom.container = null;
        this.d2cZoom.image = null;
        this.d2cZoom.scale = 1;
        this.d2cZoom.x = 0;
        this.d2cZoom.y = 0;
      }

      resetD2cZoom() {
        this.d2cPointers.clear();
        this.d2cZoom.scale = 1;
        this.d2cZoom.x = 0;
        this.d2cZoom.y = 0;
        this.applyD2cTransform();
      }

      onD2cPointerDown(event) {
        const image = this.d2cZoom.image;
        if (!image || event.pointerType === 'mouse') return;

        event.preventDefault();
        this.d2cPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        try {
          image.setPointerCapture?.(event.pointerId);
        } catch (error) {
          // Synthetic touch events and some mobile browsers can reject capture; pan/zoom still works without it.
        }

        if (this.d2cPointers.size === 1) {
          this.d2cZoom.panStartX = event.clientX;
          this.d2cZoom.panStartY = event.clientY;
          this.d2cZoom.startX = this.d2cZoom.x;
          this.d2cZoom.startY = this.d2cZoom.y;
        } else if (this.d2cPointers.size === 2) {
          this.d2cZoom.startDistance = this.getD2cPointerDistance();
          this.d2cZoom.startScale = this.d2cZoom.scale;
        }
      }

      onD2cPointerMove(event) {
        if (!this.d2cPointers.has(event.pointerId)) return;

        event.preventDefault();
        this.d2cPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.d2cPointers.size >= 2) {
          const distance = this.getD2cPointerDistance();
          const nextScale = this.d2cZoom.startScale * (distance / Math.max(this.d2cZoom.startDistance, 1));
          this.d2cZoom.scale = this.clampD2c(nextScale, 1, 4);
          if (this.d2cZoom.scale <= 1.01) {
            this.d2cZoom.scale = 1;
            this.d2cZoom.x = 0;
            this.d2cZoom.y = 0;
          }
        } else if (this.d2cZoom.scale > 1) {
          this.d2cZoom.x = this.d2cZoom.startX + event.clientX - this.d2cZoom.panStartX;
          this.d2cZoom.y = this.d2cZoom.startY + event.clientY - this.d2cZoom.panStartY;
        }

        this.clampD2cPan();
        this.applyD2cTransform();
      }

      onD2cPointerEnd(event) {
        this.d2cPointers.delete(event.pointerId);
        if (this.d2cPointers.size === 1) {
          const pointer = Array.from(this.d2cPointers.values())[0];
          this.d2cZoom.panStartX = pointer.x;
          this.d2cZoom.panStartY = pointer.y;
          this.d2cZoom.startX = this.d2cZoom.x;
          this.d2cZoom.startY = this.d2cZoom.y;
        }
      }

      getD2cPointerDistance() {
        const pointers = Array.from(this.d2cPointers.values());
        if (pointers.length < 2) return 0;
        return Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y);
      }

      clampD2cPan() {
        const image = this.d2cZoom.image;
        const container = this.d2cZoom.container;
        if (!image || !container || this.d2cZoom.scale <= 1) {
          this.d2cZoom.x = 0;
          this.d2cZoom.y = 0;
          return;
        }

        const maxX = Math.max(0, (image.clientWidth * this.d2cZoom.scale - container.clientWidth) / 2 + 24);
        const maxY = Math.max(0, (image.clientHeight * this.d2cZoom.scale - container.clientHeight) / 2 + 24);
        this.d2cZoom.x = this.clampD2c(this.d2cZoom.x, -maxX, maxX);
        this.d2cZoom.y = this.clampD2c(this.d2cZoom.y, -maxY, maxY);
      }

      applyD2cTransform() {
        const image = this.d2cZoom.image;
        if (!image) return;

        image.style.transform = `translate3d(${this.d2cZoom.x}px, ${this.d2cZoom.y}px, 0) scale(${this.d2cZoom.scale})`;
        this.classList.toggle('product-media-modal--zoomed', this.d2cZoom.scale > 1);
      }

      clampD2c(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}
